import React, { useEffect, useState } from "react";
import axios from "axios";
import {
	ComposedChart,
	Bar,
	Line,
	XAxis,
	YAxis,
	Tooltip,
	Legend,
	ResponsiveContainer,
} from "recharts";
import { toast } from "react-toastify";
import { Player } from "@lottiefiles/react-lottie-player";
import loaderAnimation from "../assets/animation/animate.json";
import { FaSms, FaFileAlt, FaClock, FaCalendarAlt } from "react-icons/fa";

const Dashboard = () => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
		const token = sessionStorage.getItem("token");

		useEffect(() => {
		const fetchData = () => {
			axios
				.get(
					"https://ecobank.approot.ng/dashboard/dashboard.php",
					{
						headers: {
							Authorization: `Bearer ${token}`,
						},
					}
				)
				.then((res) => {
					const cleanedData = mergeGloNetworks(res.data);
					setData(cleanedData);
					setLoading(false);
				})
				.catch(() => {
					toast.error("Failed to fetch dashboard data");
					setLoading(false);
				});
		};

		fetchData();

		const interval = setInterval(fetchData, 120000);

		return () => clearInterval(interval);
	}, [token]);

	const mergeGloNetworks = (list) => {
		const merged = {};
		list.forEach((item) => {
			const name = item.network.toLowerCase();
			if (name === "glo" || name === "globacom") {
				if (!merged["Glo"]) {
					merged["Glo"] = { ...item, network: "Glo" };
				} else {
					Object.keys(item).forEach((key) => {
						if (key !== "network" && key !== "id") {
							merged["Glo"][key] += item[key];
						}
					});
				}
			} else {
				merged[item.network] = item;
			}
		});
		return Object.values(merged);
	};

	const getTotalPerNetwork = (network) =>
		network.delivered +
		network.pending +
		network.expired +
		network.undelivered +
		network.rejected;

	const getTotal = (key) => data.reduce((acc, d) => acc + (d[key] || 0), 0);
	const currentDate = new Date().toLocaleDateString();

	const getBgColor = (network) => {
		const total = getTotalPerNetwork(network);
		if (total === 0) return { delivered: "", failed: "", pending: "" };

		const deliveredRate = (network.delivered / total) * 100;
		const failedRate =
			((network.expired + network.undelivered + network.rejected) / total) *
			100;
		const pendingRate = (network.pending / total) * 100;

		const getColor = (rate, green, yellow, inverse = false) => {
			if (!inverse) {
				if (rate >= green) return "bg-green-600";
				if (rate >= yellow) return "bg-yellow-600";
				return "bg-red-600";
			}
			if (rate <= green) return "bg-green-600";
			if (rate <= yellow) return "bg-yellow-600";
			return "bg-red-600";
		};

		return {
			delivered: getColor(deliveredRate, 90, 75),
			failed: getColor(failedRate, 2, 5, true),
			pending: getColor(pendingRate, 5, 10, true),
		};
	};

	if (loading) {
		return (
			<div className='flex justify-center items-center h-[70vh] flex-col'>
				<Player
					autoplay
					loop
					src={loaderAnimation}
					style={{ height: 300, width: 300 }}
				/>
			</div>
		);
	}

	if (!loading && data.length === 0) {
		return (
			<div className='flex flex-col justify-center items-center h-[70vh] text-gray-500'>
				<p className='text-xl font-semibold'>No data available</p>
				<p className='text-sm'>Please check back later or try refreshing.</p>
			</div>
		);
	}

	return (
		<div className='space-y-10 p-4'>
			{/* Summary Cards */}
			<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'>
				<Card
					icon={<FaSms className='text-3xl text-blue-600' />}
					title='Total Messages'
					value={(
						getTotal("delivered") +
						getTotal("pending") +
						getTotal("expired") +
						getTotal("undelivered") +
						getTotal("rejected")
					).toLocaleString()}
				/>
				<Card
					icon={<FaFileAlt className='text-3xl text-green-600' />}
					title='Total Pages'
					value={getTotal("pages").toLocaleString()}
				/>
				<Card
					icon={<FaClock className='text-3xl text-yellow-600' />}
					title='Total Pending'
					value={getTotal("pending").toLocaleString()}
				/>
				<Card
					icon={<FaCalendarAlt className='text-3xl text-indigo-600' />}
					title='Date'
					value={currentDate}
				/>
			</div>

			{/* Table with percentages & health */}
			<div className='bg-white p-6 rounded-xl shadow-lg overflow-auto border border-gray-200 mt-8'>
				<h2 className='text-xl font-semibold mb-6'>
					Network Distribution & Health
				</h2>
				<table className='min-w-full border-collapse'>
					<thead className='bg-gray-100 text-gray-700'>
						<tr>
							{[
								"Network",
								"Delivered",
								"Pending",
								"Expired",
								"Undelivered",
								"Rejected",
								"Total",
							].map((h) => (
								<th
									key={h}
									className='p-3 border border-gray-300 text-left text-sm'
								>
									{h}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{data.map((d) => {
							const total = getTotalPerNetwork(d);
							const bg = getBgColor(d);
							return (
								<tr key={d.network} className='hover:bg-gray-50 text-sm'>
									<td className='p-3 border '>{d.network}</td>
									<td className={`p-3 border text-white ${bg.delivered}`}>
										{d.delivered} ({((d.delivered / total) * 100).toFixed(1)}%)
									</td>
									<td className={`p-3 border text-white ${bg.pending}`}>
										{d.pending} ({((d.pending / total) * 100).toFixed(1)}%)
									</td>
									<td className={`p-3 border text-white ${bg.failed}`}>
										{d.expired} ({((d.expired / total) * 100).toFixed(1)}%)
									</td>
									<td className={`p-3 border text-white ${bg.failed}`}>
										{d.undelivered} (
										{((d.undelivered / total) * 100).toFixed(1)}%)
									</td>
									<td className={`p-3 border text-white ${bg.failed}`}>
										{d.rejected} ({((d.rejected / total) * 100).toFixed(1)}%)
									</td>
									<td className='p-3 border  font-semibold'>
										{total.toLocaleString()}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{/* Composed Chart */}
			<div className='bg-white p-6 rounded-xl shadow-lg mt-10'>
				<h2 className='text-xl font-semibold mb-6'>Network SMS Summary</h2>
				<ResponsiveContainer width='100%' height={380}>
					<ComposedChart data={data}>
						<XAxis dataKey='network' />
						<YAxis />
						<Tooltip
							content={({ active, payload, label }) =>
								active &&
								payload && (
									<div className='bg-white p-4 rounded-lg shadow-lg border text-sm space-y-1'>
										<p className='font-bold text-lg'>{label}</p>
										{payload.map((e, i) => (
											<p key={i} className='flex items-center space-x-2'>
												<span
													className='inline-block w-3 h-3 rounded-full'
													style={{ backgroundColor: e.color }}
												></span>
												<span className='text-gray-700 font-medium'>
													{e.name}:
												</span>
												<span className='text-gray-600'>
													{e.value.toLocaleString()}
												</span>
											</p>
										))}
									</div>
								)
							}
						/>
						<Legend />
						<Bar dataKey='delivered' name='Delivered' fill='#4caf50' />
						<Bar dataKey='pending' name='Pending' fill='#ff9800' />
						<Bar dataKey='rejected' name='Rejected' fill='#f44336' />
						<Line
							type='monotone'
							dataKey={(d) =>
								Number(((d.delivered / getTotalPerNetwork(d)) * 100).toFixed(1))
							}
							name='Delivery Rate (%)'
							stroke='#2196f3'
							strokeWidth={2}
						/>
					</ComposedChart>
				</ResponsiveContainer>
			</div>
		</div>
	);
};

// Reusable Card component
const Card = ({ icon, title, value }) => (
	<div className='bg-white p-5 rounded-xl shadow flex items-center space-x-4 hover:shadow-md transition-shadow'>
		{icon}
		<div>
			<p className='text-sm text-gray-500'>{title}</p>
			<p className='text-2xl font-bold'>{value}</p>
		</div>
	</div>
);

export default Dashboard;
